import { env } from "./env";

export type MetricName =
  | "content_autosave"
  | "content_version_save"
  | "content_publication"
  | "content_restore"
  | "stale_save_conflict"
  | "edit_lock_takeover"
  | "search_index_lag"
  | "offline_queue_age"
  | "offline_sync_latency"
  | "api_request";

type Metric = {
  name: MetricName;
  status: "success" | "failure" | "conflict";
  durationMs?: number;
  value?: number;
  operation?: string;
  environment: string;
  occurredAt: string;
};

export async function recordMetric(
  metric: Omit<Metric, "environment" | "occurredAt">,
) {
  if (!env.VITE_TELEMETRY_ENDPOINT) return;
  const payload: Metric = {
    ...metric,
    environment: env.VITE_APP_ENV,
    occurredAt: new Date().toISOString(),
  };
  // The schema deliberately has no arbitrary attributes or text fields. Content,
  // handovers, health details, user IDs and record IDs cannot be attached.
  await fetch(env.VITE_TELEMETRY_ENDPOINT, {
    method: "POST",
    keepalive: true,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

export async function measured<T>(
  name: MetricName,
  operation: string,
  action: () => Promise<T>,
) {
  const started = performance.now();
  try {
    const result = await action();
    await recordMetric({
      name,
      operation,
      status: "success",
      durationMs: performance.now() - started,
    });
    return result;
  } catch (error) {
    await recordMetric({
      name,
      operation,
      status:
        (error as { code?: string }).code === "C4090" ? "conflict" : "failure",
      durationMs: performance.now() - started,
    });
    throw error;
  }
}
